<template>
    <form id="priority-form" @submit.prevent="handleSubmit(!v$.$invalid)">
        <div class="grid">
            <div class="col-12">
                <as-title class="title"
                    :title="$t('ficheBu.priority_form.' + (priority?.id ? 'edit_priority_title' : 'new_priority_title'))"
                    :size="3" />
            </div>
            <div class="field col-12">
                <div class="p-float-label">
                    <InputText type="text" v-model="v$.adminTitle.$model" class="w-100"
                        :class="{ 'p-invalid': v$.adminTitle.$invalid && submitted }" />
                    <label>{{ $t("ficheBu.priority_form.priority_admin_title") }}</label>
                </div>
                <as-input-error :errors="v$.adminTitle.$silentErrors" v-show="submitted" />
            </div>

            <div class="col-12">
                <div class="flex w-100">
                    <as-title :title="$t('ficheBu.priority_form.priority_history_title')" :size="3"></as-title>
                    <div class="ml-auto">
                        <Button :label="$t('ficheBu.priority_form.priority_new_history_button_label')"
                            class="button-default" @click="addHistory" />
                    </div>
                </div>
                <div class="grid">
                    <div v-for="(e, index) in state.priorityHistory" :key="index" class="col-6">
                        <Card class="bloc-card">
                            <template #header>
                                <div class="flex w-100 p-2 bg-primary-sliced">
                                    <span class="bloc-index">{{ e.date ? formatDate(e.date) : '???' }}</span>
                                    <i class="pi pi-times-circle ml-auto bloc-remove-icon" style="font-size: 28px;"
                                        @click="showConfirmModalRemoveHistory(index)"></i>
                                </div>
                            </template>
                            <template #content class="p-0">
                                <div class="grid field mt-3">
                                    <div class="col-6">
                                        <div class="p-float-label">
                                            <Calendar v-model="state.priorityHistory[index].date" view="month"
                                                dateFormat="mm/yy" class=" w-100" />
                                            <label>{{ $t("ficheBu.priority_form.priority_history_date_field") }}</label>
                                        </div>
                                    </div>
                                    <div class="col-6">
                                        <TranslateButton :stringsToTranslate="getStringsToTranslate(index)"
                                            buttonClass="w-100"
                                            @onTranslationSuccess="(translatedData, targetLangCode) => handleTranslationSuccess(index, translatedData, targetLangCode)" />
                                    </div>
                                </div>
                                <TabView>
                                    <TabPanel>
                                        <template #header>
                                            <div class="flex align-items-center gap-2">
                                                <Avatar :image="getFlag('fr')" shape="square" />
                                                <span class="font-bold white-space-nowrap">FR</span>
                                            </div>
                                        </template>
                                        <div class="mt-3">
                                            <div class="p-float-label">
                                                <InputText type="text" v-model="state.priorityHistory[index].titleFr"
                                                    class="w-100"
                                                    :class="{ 'p-invalid': v$.priorityHistory[index]?.titleFr.$invalid && state.submitted }" />
                                                <label>
                                                    {{ $t("ficheBu.priority_form.priority_history_title_field") }}
                                                </label>
                                            </div>
                                            <div class="p-float-label flex mt-5">
                                                <Textarea type="text" v-model="state.priorityHistory[index].contentFr"
                                                    rows="3" cols="30" class="w-100"
                                                    :class="{ 'p-invalid': v$.priorityHistory[index]?.contentFr.$invalid && state.submitted }" />
                                                <label>
                                                    {{
                                                        $t("ficheBu.priority_form.priority_history_content_field")
                                                    }}
                                                </label>
                                            </div>
                                        </div>
                                    </TabPanel>
                                    <TabPanel>
                                        <template #header>
                                            <div class="flex align-items-center gap-2">
                                                <div class="flex align-items-center gap-2">
                                                    <Avatar :image="getFlag('en')" shape="square" />
                                                    <span class="font-bold white-space-nowrap">EN</span>
                                                </div>
                                            </div>
                                        </template>
                                        <div class="mt-3">
                                            <div class="p-float-label">
                                                <InputText type="text" v-model="state.priorityHistory[index].titleEn"
                                                    class="w-100"
                                                    :class="{ 'p-invalid': v$.priorityHistory[index]?.titleEn.$invalid && state.submitted }" />
                                                <label>
                                                    {{ $t("ficheBu.priority_form.priority_history_title_field") }}
                                                </label>
                                            </div>
                                            <div class="p-float-label flex mt-5">
                                                <Textarea type="text" v-model="state.priorityHistory[index].contentEn"
                                                    rows="3" cols="30" class="w-100"
                                                    :class="{ 'p-invalid': v$.priorityHistory[index]?.contentEn.$invalid && state.submitted }" />
                                                <label>
                                                    {{
                                                        $t("ficheBu.priority_form.priority_history_content_field")
                                                    }}
                                                </label>
                                            </div>
                                        </div>
                                    </TabPanel>
                                </TabView>
                                <as-input-error v-if="v$.priorityHistory[index]"
                                    :errors="v$.priorityHistory[index].titleFr.$silentErrors"
                                    v-show="state.submitted" />
                            </template>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
        <div class="grid fixed-bottom">
            <div class="col flex justify-content-end">
                <Button :label="$t('form.cancel_button')" class="button-default"
                    :icon="isSending ? 'pi pi-spin pi-spinner' : 'pi pi-times'" @click="onClose"
                    :disabled="isSending" />
                <Button type="submit" :label="$t('form.submit_button')" class="button-primary ml-2"
                    :icon="isSending ? 'pi pi-spin pi-spinner' : 'pi pi-check'" :disabled="isSending" />
            </div>
        </div>
    </form>
    <as-confirm-modal :isVisible="showRemoveHistoryConfirmModal"
        :modalMessage="$t('rituals.ritual_form.ritual_remove_subject_confirm_message')" @onConfirm="removeHistory"
        @onCancel="cancelConfirmModalRemoveHistory" />
</template>

<script>
import useVuelidate from "@vuelidate/core"
import { required } from "@vuelidate/validators"
import { reactive, computed, watch } from "vue"

import AsConfirmModal from "@/components/Modals/AsConfirmModal.vue";
import AsInputError from "../Forms/AsInputError.vue"
import AsTitle from "../Elements/AsTitle.vue"
import TranslateButton from "../Elements/TranslateButton.vue";

import Avatar from "primevue/avatar";
import Button from "primevue/button"
import Card from "primevue/card";
import Calendar from "primevue/calendar";
import InputText from "primevue/inputtext"
import ProgressSpinner from "primevue/progressspinner";
import Textarea from "primevue/textarea";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

import moment from "moment"
import { translate } from "../../services/googleTranslateService";

export default {
    name: "PriorityForm",
    components: {
        AsConfirmModal,
        AsInputError,
        AsTitle,
        TranslateButton,
        Avatar,
        Button,
        Card,
        Calendar,
        InputText,
        ProgressSpinner,
        Textarea,
        TabView,
        TabPanel
    },
    props: {
        priority: Object,
        isSending: Boolean,
    },
    setup(props) {
        const state = reactive({
            adminTitle: "",
            priorityHistory: [{ date: null, titleFr: "", titleEn: "", contentFr: "", contentEn: "" }],
            submitted: false,
        })
        const rules = computed(() => ({
            adminTitle: { required },
            priorityHistory: state.priorityHistory.map(() => ({
                date: {},
                titleFr: {},
                titleEn: {},
                contentFr: {},
                contentEn: {}
            })),
        }))
        const v$ = useVuelidate(rules, state)
        watch(() => props.priority, (priority) => {
            if (priority) {
                state.adminTitle = priority.adminTitle || "";
                state.priorityHistory = priority.priorityHistory ?
                    priority.priorityHistory.map(e => ({ ...e, date: new Date(e.date) })) :
                    [{ id: 0, date: new Date(), titleFr: "", titleEn: "", contentFr: "", contentEn: "" }];
            }
        }, { deep: true, immediate: true });
        return { v$, state }
    },
    computed: {
        locale() {
            return this.$i18n.locale;
        },
    },
    data() {
        return {
            submitted: false,
            showRemoveHistoryConfirmModal: false,
            historyIndexToRemove: null,
        }
    },
    validations() {
        return {
            titleFr: { required },
            titleEn: { required },
            contentFr: { required },
            contentEn: { required },
        }
    },
    emits: ["onClose", "onSubmit"],
    methods: {
        handleSubmit(isFormValid) {
            this.submitted = true
            if (!isFormValid) {
                return;
            }
            const priority = {
                id: this.priority.id,
                adminTitle: this.state.adminTitle,
                priorityHistory: [...this.state.priorityHistory].filter(e => e.titleFr.trim() !== "" && e.date !== null)
            }
            this.submitted = false
            this.$emit("onSubmit", priority)
        },
        onClose() {
            this.state.adminTitle = ""
            this.state.priorityHistory = []
            this.submitted = false
            this.$emit("onClose");
        },
        addHistory() {
            const maxDate = this.state.priorityHistory?.length >= 0 ?
                moment(
                    [...this.state.priorityHistory].filter(e => !!e.date).sort((a, b) =>
                        moment(b.date).toDate().getTime() - moment(a.date).toDate().getTime()
                    )[0]?.date || new Date()
                ).add(1, 'month').toDate() :
                new Date()
            const sortedHistories = [...this.state.priorityHistory, { id: 0, date: maxDate, titleFr: "", titleEn: "", contentFr: "", contentEn: "" }].sort((a, b) =>
                moment(b.date).toDate().getTime() - moment(a.date).toDate().getTime()
            )
            this.state.priorityHistory = sortedHistories
        },
        removeHistory() {
            this.showRemoveHistoryConfirmModal = false
            if (!this.historyIndexToRemove === null) { return }
            this.state.priorityHistory.splice(this.historyIndexToRemove, 1)
            this.historyIndexToRemove = null
        },
        showConfirmModalRemoveHistory(index) {
            this.showRemoveHistoryConfirmModal = true
            this.historyIndexToRemove = index
        },
        cancelConfirmModalRemoveHistory() {
            this.showRemoveHistoryConfirmModal = false
            this.historyIndexToRemove = null
        },
        formatDate(date) {
            return moment(date).format("MM/YYYY");
        },
        getFlag(iso) {
            return new URL(`../../assets/images/flags/${iso}.svg`, import.meta.url).href;
        },
        getStringsToTranslate(index) {
            const historyToTranslate = this.state.priorityHistory[index];
            const targetTranslate = this.locale === 'fr' ? 'en' : 'fr';
            return targetTranslate === 'fr' ?
                [historyToTranslate.titleEn, historyToTranslate.contentEn] :
                [historyToTranslate.titleFr, historyToTranslate.contentFr];
        },
        handleTranslationSuccess(index, translatedData, targetLangCode) {
            if (Array.isArray(translatedData)) {
                switch (targetLangCode) {
                    case 'en':
                        this.state.priorityHistory[index].titleEn = translatedData[0];
                        this.state.priorityHistory[index].contentEn = translatedData[1];
                        break;
                    case 'fr':
                        this.state.priorityHistory[index].titleFr = translatedData[0];
                        this.state.priorityHistory[index].contentFr = translatedData[1];
                        break;
                }
            }
        },
    },
};
</script>



<style lang="scss">
@import "../../assets/styles/colors.scss";

#priority-form {
    .bloc-card {
        .bloc-index {
            width: 100px;
        }
    }

    .p-tabview-panels {
        padding-left: 0;
        padding-right: 0;
    }
}
</style>
